(() => {
  if (window.__mcsThemeRuntimeInitialized) return;
  window.__mcsThemeRuntimeInitialized = true;

  const root = document.documentElement;
  const body = document.body;

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const overlay = $('[data-drawer-overlay]');
  const mobileDrawer = $('[data-mobile-drawer]');
  const cartDrawer = $('[data-cart-drawer]');
  const searchDrawer = $('[data-search-drawer]');
  const quickViewModal = $('[data-quick-view-modal]');
  const zoomModal = $('[data-image-zoom-modal]');
  const cartDrawerBody = $('[data-cart-drawer-body]');
  const cartDrawerCount = $('[data-cart-drawer-count]');
  const cartSubtotal = $('[data-cart-subtotal]');
  const cartEmpty = $('[data-cart-empty]');
  const searchInput = $('[data-search-input]');
  const searchResults = $('[data-search-results]');
  const quickViewContent = $('[data-quick-view-content]');
  const zoomImage = $('[data-image-zoom-image]');
  const cartCountNodes = $$('[data-cart-count]');
  const loginModal = $('[data-login-modal]');
  const loginModalPanel = $('[data-login-modal-panel]');

  let cartCache = null;
  let searchTimer = null;
  let activePanel = null;
  let lastFocusedElement = null;
  let ambientFrame = 0;
  const ambientPoint = { x: 0.5, y: 0.35 };
  const focusableSelector = 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const moneyCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: moneyCurrency
  });

  const formatMoney = (cents) => moneyFormatter.format(Number(cents || 0) / 100);
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  const stripHtml = (value) => String(value ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  const setTheme = (value) => {
    root.dataset.theme = value;
    localStorage.setItem('mcs-theme', value);
  };

  const showPanel = (panel) => {
    if (!panel) return;
    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activePanel = panel;
    panel.hidden = false;
    if (overlay) overlay.hidden = false;
    body.classList.add('drawer-open');
  };

  const hidePanel = (panel) => {
    if (panel) panel.hidden = true;
  };

  const closeAllPanels = () => {
    [mobileDrawer, cartDrawer, searchDrawer, quickViewModal, zoomModal].forEach(hidePanel);
    if (overlay) overlay.hidden = true;
    body.classList.remove('drawer-open');
    activePanel = null;
    if (lastFocusedElement && document.contains(lastFocusedElement)) {
      lastFocusedElement.focus({ preventScroll: true });
    }
    lastFocusedElement = null;
  };

  const cartItemImage = (item) => {
    if (!item) return '';
    if (typeof item.image === 'string') return item.image;
    if (item.image && typeof item.image === 'object' && item.image.src) return item.image.src;
    if (typeof item.featured_image === 'string') return item.featured_image;
    if (item.featured_image && typeof item.featured_image === 'object' && item.featured_image.src) return item.featured_image.src;
    return '';
  };

  const updateCartBadges = (cart) => {
    const count = Number(cart?.item_count || 0);
    cartCountNodes.forEach((node) => {
      node.textContent = String(count);
    });
    if (cartDrawerCount) {
      cartDrawerCount.textContent = `${count} item${count === 1 ? '' : 's'}`;
    }
    if (cartSubtotal) {
      cartSubtotal.textContent = formatMoney(cart?.total_price || 0);
    }
  };

  const renderCartDrawer = (cart) => {
    cartCache = cart;
    updateCartBadges(cart);

    if (!cartDrawerBody) return;

    if (!cart?.items?.length) {
      cartDrawerBody.innerHTML = '<div class="drawer-shell__empty">Your cart is empty right now. Add a product to see it here.</div>';
      if (cartEmpty) cartEmpty.hidden = false;
      return;
    }

    if (cartEmpty) cartEmpty.hidden = true;

    cartDrawerBody.innerHTML = `
      <div class="drawer-cart-list">
        ${cart.items.map((item, index) => {
          const imageSrc = cartItemImage(item);
          const line = index + 1;
          return `
            <div class="drawer-cart-item" data-cart-line="${line}">
              <a class="drawer-cart-item__media" href="${escapeHtml(item.url || '#')}">
                ${imageSrc ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(item.product_title || 'Product')}">` : '<div class="drawer-cart-item__placeholder"></div>'}
              </a>
              <div class="drawer-cart-item__body">
                <strong>${escapeHtml(item.product_title || item.title || 'Product')}</strong>
                <span>${escapeHtml(item.variant_title || '')}</span>
                <small>${formatMoney(item.final_line_price || 0)}</small>
              </div>
              <div class="drawer-cart-item__controls">
                <button type="button" class="drawer-cart-item__step" data-cart-decrease aria-label="Decrease quantity">-</button>
                <input type="number" min="0" value="${Number(item.quantity || 1)}" class="drawer-cart-item__qty" data-cart-qty>
                <button type="button" class="drawer-cart-item__step" data-cart-increase aria-label="Increase quantity">+</button>
                <button type="button" class="drawer-cart-item__remove" data-cart-remove>Remove</button>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      throw new Error('Unable to load cart');
    }
    return response.json();
  };

  const refreshCart = async ({ open = false } = {}) => {
    const cart = await fetchCart();
    renderCartDrawer(cart);
    if (open) {
      showPanel(cartDrawer);
    }
    return cart;
  };

  const addItems = async (items, { open = true } = {}) => {
    if (!items.length) return;
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      throw new Error('Unable to add items');
    }

    closeAllPanels();
    await refreshCart({ open });
  };

  const changeLine = async (line, quantity) => {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) {
      throw new Error('Unable to update cart');
    }

    const cart = await response.json();
    renderCartDrawer(cart);
    if (activePanel === cartDrawer && cart.items.length === 0 && cartEmpty) {
      cartEmpty.hidden = false;
    }
    if (document.querySelector('[data-cart-page]') && activePanel !== cartDrawer) {
      window.location.reload();
    }
  };

  const openSearch = () => {
    showPanel(searchDrawer);
    if (searchInput) {
      window.requestAnimationFrame(() => searchInput.focus({ preventScroll: true }));
      if (searchInput.value.trim().length >= 2) {
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  const openCart = async () => {
    await refreshCart({ open: true });
  };

  const renderSearchPlaceholder = (message) => {
    if (!searchResults) return;
    searchResults.innerHTML = `<p class="drawer-shell__empty">${escapeHtml(message || 'Start typing to see live product suggestions.')}</p>`;
  };

  const renderSearchResults = (items, term) => {
    if (!searchResults) return;

    if (!term) {
      renderSearchPlaceholder();
      return;
    }

    if (!items.length) {
      renderSearchPlaceholder(`No products found for "${term}".`);
      return;
    }

    searchResults.innerHTML = `
      <div class="predictive-grid">
        ${items.map((item) => {
          const imageSrc = item.image || item.thumbnail || item.featured_image || '';
          const price = item.price || item.price_min || item.final_price || 0;
          const priceLabel = typeof price === 'string' && /[£$€]/.test(price) ? price : formatMoney(price);
          return `
            <a href="${escapeHtml(item.url || '#')}" class="predictive-card">
              <div class="predictive-card__media">
                ${imageSrc ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(item.title || '')}">` : '<div class="predictive-card__placeholder"></div>'}
              </div>
              <div class="predictive-card__body">
                <strong>${escapeHtml(item.title || '')}</strong>
                <span>${escapeHtml(item.vendor || '')}</span>
                <small>${escapeHtml(priceLabel)}</small>
              </div>
            </a>
          `;
        }).join('')}
      </div>
      <a class="btn btn--solid btn--full" href="/search?q=${encodeURIComponent(term)}">View all results</a>
    `;
  };

  const fetchSearchSuggestions = async (term) => {
    const endpoint = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[limit_scope]=each`;
    const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      throw new Error('Unable to fetch search suggestions');
    }
    const data = await response.json();
    return (
      data?.resources?.results?.products ||
      data?.resources?.results?.items ||
      data?.resources?.results ||
      []
    );
  };

  const openQuickView = async (handle) => {
    if (!quickViewContent) return;
    quickViewContent.innerHTML = '<div class="drawer-shell__empty">Loading product preview...</div>';
    showPanel(quickViewModal);

    try {
      const response = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        throw new Error('Unable to fetch product');
      }
      const product = await response.json();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariant = variants.find((variant) => variant.available) || variants[0] || {};
      const imageSrc = product.featured_image || product.images?.[0] || '';
      quickViewContent.innerHTML = `
        <div class="quick-view-card">
          <div class="quick-view-card__media">
            ${imageSrc ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(product.title || '')}">` : '<div class="quick-view-card__placeholder"></div>'}
          </div>
          <div class="quick-view-card__body">
            <p class="eyebrow">${escapeHtml(product.vendor || '')}</p>
            <h3>${escapeHtml(product.title || '')}</h3>
            <p class="quick-view-card__price" data-qv-price>${formatMoney(availableVariant.price || 0)}</p>
            <p class="quick-view-card__copy">${escapeHtml(stripHtml(product.description || '').slice(0, 240))}</p>
            ${variants.length > 1 ? `
              <label class="quick-view-card__field">
                <span>Variant</span>
                <select class="field-input" data-qv-variant-select>
                  ${variants.map((variant) => `
                    <option
                      value="${variant.id}"
                      data-price="${variant.price || 0}"
                      data-available="${variant.available ? 'true' : 'false'}"
                      ${variant.id === availableVariant.id ? 'selected' : ''}
                    >
                      ${escapeHtml(variant.title || 'Default')} - ${formatMoney(variant.price || 0)}
                    </option>
                  `).join('')}
                </select>
              </label>
            ` : ''}
            <div class="quick-view-card__actions">
              <button type="button" class="btn btn--solid" data-qv-add data-variant-id="${availableVariant.id}" ${availableVariant.available === false ? 'disabled' : ''}>
                ${availableVariant.available === false ? 'Sold Out' : 'Add to Cart'}
              </button>
              <a class="btn btn--ghost" href="/products/${handle}">Open product</a>
            </div>
          </div>
        </div>
      `;

      const variantSelect = $('[data-qv-variant-select]', quickViewContent);
      const qvPrice = $('[data-qv-price]', quickViewContent);
      const qvAdd = $('[data-qv-add]', quickViewContent);

      const syncQuickView = () => {
        const selected = variantSelect?.selectedOptions?.[0];
        if (!selected || !qvAdd || !qvPrice) return;
        const available = selected.dataset.available === 'true';
        const price = Number(selected.dataset.price || 0);
        qvPrice.textContent = formatMoney(price);
        qvAdd.disabled = !available;
        qvAdd.textContent = available ? 'Add to Cart' : 'Sold Out';
        qvAdd.dataset.variantId = selected.value;
      };

      variantSelect?.addEventListener('change', syncQuickView);
      qvAdd?.addEventListener('click', async () => {
        const variantId = Number(qvAdd.dataset.variantId || 0);
        if (!variantId) return;
        qvAdd.disabled = true;
        try {
          await addItems([{ id: variantId, quantity: 1 }], { open: true });
        } finally {
          qvAdd.disabled = false;
        }
      });
      syncQuickView();
    } catch (error) {
      quickViewContent.innerHTML = '<div class="drawer-shell__empty">Unable to load this product right now.</div>';
      console.error(error);
    }
  };

  const openZoom = (src, alt) => {
    if (!zoomImage) return;
    zoomImage.src = src;
    zoomImage.alt = alt || '';
    showPanel(zoomModal);
  };

  const setupHeroSlider = (slider) => {
    const slides = $$('.hero-slide', slider);
    const dots = $$('[data-hero-dot]', slider);
    if (!slides.length) return;

    let index = 0;

    const render = () => {
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    };

    $('[data-hero-prev]', slider)?.addEventListener('click', () => {
      index = (index - 1 + slides.length) % slides.length;
      render();
    });

    $('[data-hero-next]', slider)?.addEventListener('click', () => {
      index = (index + 1) % slides.length;
      render();
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        index = Number(dot.dataset.heroDot || 0);
        render();
      });
    });

    let startX = 0;
    slider.addEventListener('touchstart', (event) => {
      startX = event.touches[0]?.clientX || 0;
    }, { passive: true });

    slider.addEventListener('touchend', (event) => {
      const endX = event.changedTouches[0]?.clientX || 0;
      const delta = endX - startX;
      if (Math.abs(delta) < 40) return;
      index = delta < 0 ? (index + 1) % slides.length : (index - 1 + slides.length) % slides.length;
      render();
    }, { passive: true });

    render();
  };

  const setupCountdown = (rootEl) => {
    const deadline = new Date(rootEl.dataset.deadline).getTime();
    if (Number.isNaN(deadline)) return;

    const tick = () => {
      const diff = Math.max(0, deadline - Date.now());
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const setText = (selector, value) => {
        const el = rootEl.querySelector(selector);
        if (el) el.textContent = String(value).padStart(2, '0');
      };
      setText('[data-days]', days);
      setText('[data-hours]', hours);
      setText('[data-minutes]', minutes);
      setText('[data-seconds]', seconds);
    };

    tick();
    window.setInterval(tick, 1000);
  };

  const syncProductForm = (wrapper) => {
    const form = $('[data-enhanced-product-form]', wrapper);
    if (!form) return;

    const variantInput = $('[data-variant-id]', form);
    const variantSelect = $('[data-variant-select]', form);
    const priceNode = $('.product-shell__price', wrapper);
    const totalNode = $('[data-total-price]', form);
    const stockNode = $('[data-stock-status]', form);
    const addToCartNode = $('[data-add-to-cart]', form);
    const accessoryChecks = $$('[data-accessory-checkbox]', form);
    const moneyFromNode = () => Number(variantInput?.dataset.variantPrice || priceNode?.dataset.basePrice || variantSelect?.selectedOptions?.[0]?.dataset.price || 0);

    const syncTotals = () => {
      const base = moneyFromNode();
      const extras = accessoryChecks.reduce((sum, check) => sum + (check.checked ? Number(check.dataset.price || 0) : 0), 0);
      if (priceNode) {
        priceNode.textContent = formatMoney(base);
        priceNode.dataset.basePrice = String(base);
      }
      if (totalNode) totalNode.textContent = formatMoney(base + extras);
    };

    const syncVariantState = (id, price, available) => {
      if (variantInput) {
        variantInput.value = id;
        variantInput.dataset.variantPrice = price;
      }
      if (variantSelect) {
        Array.from(variantSelect.options).forEach((option) => {
          option.selected = option.value === String(id);
        });
      }
      if (stockNode) {
        stockNode.textContent = String(available) === 'true' ? 'In stock' : 'Sold out';
      }
      if (addToCartNode) {
        addToCartNode.disabled = String(available) !== 'true';
        addToCartNode.textContent = String(available) === 'true' ? 'Add to Cart' : 'Sold Out';
      }
      $$('[data-condition-pill]', form).forEach((pill) => {
        pill.classList.toggle('is-active', pill.dataset.variantId === String(id));
      });
      syncTotals();
    };

    $$('[data-condition-pill]', form).forEach((pill) => {
      pill.addEventListener('click', () => {
        syncVariantState(pill.dataset.variantId, pill.dataset.variantPrice, pill.dataset.variantAvailable);
      });
    });

    variantSelect?.addEventListener('change', () => {
      const selected = variantSelect.selectedOptions[0];
      if (!selected) return;
      syncVariantState(selected.value, selected.dataset.price, selected.dataset.available);
    });

    accessoryChecks.forEach((check) => {
      check.addEventListener('change', syncTotals);
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variantId = Number(variantInput?.value || 0);
      if (!variantId) return;

      const items = [{ id: variantId, quantity: 1 }];
      accessoryChecks.filter((check) => check.checked).forEach((check) => {
        items.push({ id: Number(check.value), quantity: 1 });
      });

      if (addToCartNode) addToCartNode.disabled = true;
      try {
        await addItems(items, { open: true });
      } catch (error) {
        console.error(error);
      } finally {
        if (addToCartNode) addToCartNode.disabled = false;
      }
    });

    syncTotals();
  };

  const setupProductGallery = () => {
    $$('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const gallery = thumb.closest('.product-gallery');
        const main = gallery?.querySelector('[data-gallery-main]');
        const imageSrc = thumb.dataset.imageSrc || '';
        if (!gallery || !main || !imageSrc) return;
        if (main.tagName === 'IMG') {
          main.src = imageSrc;
          main.dataset.zoomSrc = imageSrc;
        }
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  };

  const setupMobileMenu = () => {
    $('[data-menu-toggle]')?.addEventListener('click', () => {
      if (!mobileDrawer) return;
      const shouldOpen = mobileDrawer.hidden;
      closeAllPanels();
      if (shouldOpen) {
        showPanel(mobileDrawer);
      }
    });
  };

  const setupGlobalClicks = () => {
    document.addEventListener('click', (event) => {
      const themeToggle = event.target.closest('[data-theme-toggle]');
      if (themeToggle) {
        const nextTheme = root.dataset.theme === 'dark' ? 'light' : 'dark';
        setTheme(nextTheme);
        return;
      }

      if (event.target.closest('[data-search-toggle]')) {
        closeAllPanels();
        openSearch();
        return;
      }

      if (event.target.closest('[data-cart-toggle]')) {
        closeAllPanels();
        void openCart();
        return;
      }

      if (event.target.closest('[data-cart-close]') || event.target.closest('[data-search-close]') || event.target.closest('[data-quick-view-close]') || event.target.closest('[data-image-zoom-close]')) {
        closeAllPanels();
        return;
      }

      if (event.target === mobileDrawer) {
        closeAllPanels();
        return;
      }

      const quickViewButton = event.target.closest('[data-quick-view]');
      if (quickViewButton) {
        closeAllPanels();
        void openQuickView(quickViewButton.dataset.productHandle);
        return;
      }

      const quickAddButton = event.target.closest('[data-quick-add]');
      if (quickAddButton) {
        const variantId = Number(quickAddButton.dataset.variantId || 0);
        if (!variantId) return;
        quickAddButton.disabled = true;
        void addItems([{ id: variantId, quantity: 1 }], { open: true }).catch((error) => {
          console.error(error);
        }).finally(() => {
          quickAddButton.disabled = false;
        });
        return;
      }

      const galleryZoom = event.target.closest('[data-image-zoom-trigger]');
      if (galleryZoom) {
        const src = galleryZoom.currentSrc || galleryZoom.src || galleryZoom.dataset.zoomSrc;
        if (!src) return;
        closeAllPanels();
        openZoom(src, galleryZoom.alt || '');
        return;
      }

      const cartRemove = event.target.closest('[data-cart-remove]');
      if (cartRemove) {
        const row = cartRemove.closest('[data-cart-line]');
        const line = Number(row?.dataset.cartLine || 0);
        if (line) {
          void changeLine(line, 0).catch((error) => console.error(error));
        }
        return;
      }

      const cartInc = event.target.closest('[data-cart-increase]');
      if (cartInc) {
        const row = cartInc.closest('[data-cart-line]');
        const line = Number(row?.dataset.cartLine || 0);
        const qtyInput = row?.querySelector('[data-cart-qty]');
        const qty = Number(qtyInput?.value || 0) + 1;
        if (line) {
          void changeLine(line, qty).catch((error) => console.error(error));
        }
        return;
      }

      const cartDec = event.target.closest('[data-cart-decrease]');
      if (cartDec) {
        const row = cartDec.closest('[data-cart-line]');
        const line = Number(row?.dataset.cartLine || 0);
        const qtyInput = row?.querySelector('[data-cart-qty]');
        const qty = Math.max(0, Number(qtyInput?.value || 0) - 1);
        if (line) {
          void changeLine(line, qty).catch((error) => console.error(error));
        }
      }
    });
  };

  const setupCartQuantityInputs = () => {
    document.addEventListener('change', (event) => {
      const qtyInput = event.target.closest('[data-cart-qty]');
      if (!qtyInput) return;
      const row = qtyInput.closest('[data-cart-line]');
      const line = Number(row?.dataset.cartLine || 0);
      const quantity = Math.max(0, Number(qtyInput.value || 0));
      if (line) {
        void changeLine(line, quantity).catch((error) => console.error(error));
      }
    });
  };

  const setupSearchDrawer = () => {
    if (!searchInput || !searchResults) return;

    searchInput.addEventListener('input', () => {
      const term = searchInput.value.trim();
      window.clearTimeout(searchTimer);

      if (term.length < 2) {
        renderSearchPlaceholder();
        return;
      }

      renderSearchPlaceholder('Searching products...');
      searchTimer = window.setTimeout(async () => {
        try {
          const results = await fetchSearchSuggestions(term);
          renderSearchResults(results, term);
        } catch (error) {
          renderSearchPlaceholder('Unable to load live suggestions right now.');
          console.error(error);
        }
      }, 250);
    });
  };

  const setupOverlay = () => {
    overlay?.addEventListener('click', () => {
      closeAllPanels();
    });
  };

  const setupKeyboardHandling = () => {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (loginModal && !loginModal.hidden) {
          closeLoginModal();
          return;
        }
        if (activePanel) {
          closeAllPanels();
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const activeDialog = loginModal && !loginModal.hidden ? loginModalPanel : activePanel;
      if (!activeDialog) return;

      const focusable = $$(focusableSelector, activeDialog).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const markLoginModalSeen = () => {
    try {
      sessionStorage.setItem('mcs-login-modal-seen', 'true');
    } catch (error) {
      root.dataset.loginModalStorage = 'unavailable';
    }
  };

  const closeLoginModal = () => {
    if (!loginModal) return;
    markLoginModalSeen();
    loginModal.hidden = true;
    body.classList.remove('login-modal-open');
    if (lastFocusedElement && document.contains(lastFocusedElement)) {
      lastFocusedElement.focus({ preventScroll: true });
    }
    lastFocusedElement = null;
  };

  const setupLoginModal = () => {
    if (!loginModal || !loginModalPanel) return;
    if (!body.classList.contains('template-index')) return;

    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem('mcs-login-modal-seen') === 'true';
    } catch (error) {
      alreadySeen = false;
    }

    loginModal.addEventListener('click', (event) => {
      if (event.target === loginModal || event.target.closest('[data-login-modal-close]')) {
        closeLoginModal();
      }
    });

    $$('[data-login-modal-auth]', loginModal).forEach((link) => {
      link.addEventListener('click', () => {
        markLoginModalSeen();
      });
    });

    if (alreadySeen) return;

    lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    loginModal.hidden = false;
    body.classList.add('login-modal-open');
    const firstAction = $('[data-login-modal-close]', loginModal) || $(focusableSelector, loginModal);
    window.requestAnimationFrame(() => {
      firstAction?.focus({ preventScroll: true });
    });
  };

  const setupAmbientMotion = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const syncAmbientVars = () => {
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      root.style.setProperty('--ambient-x', ambientPoint.x.toFixed(4));
      root.style.setProperty('--ambient-y', ambientPoint.y.toFixed(4));
      root.style.setProperty('--scroll-y', String(window.scrollY / maxScroll));
    };

    const requestSync = () => {
      if (ambientFrame) return;
      ambientFrame = window.requestAnimationFrame(() => {
        ambientFrame = 0;
        syncAmbientVars();
      });
    };

    syncAmbientVars();

    window.addEventListener('pointermove', (event) => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      ambientPoint.x = Math.min(1, Math.max(0, event.clientX / window.innerWidth));
      ambientPoint.y = Math.min(1, Math.max(0, event.clientY / window.innerHeight));
      requestSync();
    }, { passive: true });

    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });
  };

  const setupSurfaceTilt = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!hasFinePointer) return;

    const surfaces = $$('.dynamic-product-card, .collection-tile, .testimonial-card, .product-card, .accessory-item, .predictive-card, .search-result-card, .quality-card, .collection-list-card, .feature-strip a.feature-pill, .hero-slide__media, .story-split__visual, .story-point, .bento-card, .faq-item, .support-card__link, .product-credibility-card, .product-accordion');
    const resetSurface = (surface) => {
      surface.style.removeProperty('transform');
    };

    surfaces.forEach((surface) => {
      surface.addEventListener('pointermove', (event) => {
        const rect = surface.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        const rotateY = ((x - 0.5) * 12).toFixed(2);
        const rotateX = ((0.5 - y) * 10).toFixed(2);
        const lift = surface.matches('.hero-slide__media') ? -10 : -6;
        surface.style.transform = `perspective(1400px) translate3d(0, ${lift}px, 0) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      }, { passive: true });

      surface.addEventListener('pointerleave', () => resetSurface(surface));
      surface.addEventListener('blur', () => resetSurface(surface));
    });
  };

  const setupCartPage = () => {
    const cartPage = $('[data-cart-page]');
    if (!cartPage) return;
    cartPage.addEventListener('submit', (event) => {
      const form = event.target.closest('form');
      if (!form || form.action.indexOf('/cart') === -1) return;
    });
  };

  const init = () => {
    root.dataset.theme = localStorage.getItem('mcs-theme') || root.dataset.theme || 'light';
    closeAllPanels();
    setupMobileMenu();
    setupOverlay();
    setupGlobalClicks();
    setupKeyboardHandling();
    setupAmbientMotion();
    setupSurfaceTilt();
    setupCartQuantityInputs();
    setupSearchDrawer();
    setupLoginModal();
    setupProductGallery();
    setupCartPage();

    $$('.hero-showcase[data-hero-slider]').forEach(setupHeroSlider);
    $$('[data-countdown]').forEach(setupCountdown);
    $$('[data-product-form-wrapper]').forEach(syncProductForm);

    void refreshCart({ open: false }).catch(() => {
      if (cartCache) renderCartDrawer(cartCache);
    });

    if (searchResults) {
      renderSearchPlaceholder();
    }
  };

  init();
})();
